import { BarDatum, ResponsiveBar } from "@nivo/bar";
import React, { useRef } from "react";
import styled from "styled-components";
import { theme } from ".";
import { formatUnits } from "../../formatting/formatKiloUnits";
import {
  ChartToolTip,
  ChartToolTipBody,
  ChartToolTipHeader,
} from "../ChartTooltip";

import Tooltip from "../Tooltip";
import { ChartData, getChartLegend, legendKeyColors } from "./chart-types";
import {
  Chart,
  ChartLegend,
  ChartTooltipColorBox,
  ChartWrapper,
} from "./common";
import { NonOverflowTooltip } from "./NonOverflowTooltip";

const ResponsiveBarChart: React.FC<{
  chartData: BarDatum[];
  colors: string[];
  keys: string[];
  theme: any;
}> = ({ chartData, colors, keys, theme }) => {
  const ref = useRef<HTMLDivElement>(null);
  const showTooltip = (e) => {
    const eventCoords = e as unknown as { x: number; y: number };
    return (
      <NonOverflowTooltip
        point={{ x: eventCoords.x, y: eventCoords.y }}
        containerSize={ref.current!.getBoundingClientRect()}
      >
        <ChartToolTip>
          <ChartToolTipHeader>{e.data.time}</ChartToolTipHeader>
          {keys.map((key) => (
            <ChartToolTipBody key={key}>
              <ChartTooltipColorBox color={legendKeyColors[key]} />
              {key}:{" "}
              <strong>
                {formatUnits(
                  parseFloat(((e.data[key] as number) ?? 0)?.toFixed(1)),
                  "kWh"
                )}
              </strong>
            </ChartToolTipBody>
          ))}
        </ChartToolTip>
      </NonOverflowTooltip>
    );
  };
  const testMode = !import.meta.env.PROD && import.meta.env.VITE_TEST_MODE;
  return (
    <ChartContainer ref={ref}>
      <ResponsiveBar
        data={chartData}
        indexBy={"index"}
        groupMode="grouped"
        keys={keys}
        margin={{
          left: 60,
          bottom: 40,
          top: 10,
        }}
        padding={0.3}
        colors={colors}
        colorBy={chartData["index"]}
        borderColor={{ from: "color", modifiers: [["darker", 1.6]] }}
        axisTop={null}
        axisRight={null}
        axisLeft={{
          tickSize: 0,
          tickPadding: 5,
          legendPosition: "middle",
          legendOffset: -40,
          legend: "kWh",
          format: (value) => {
            return `${value}`;
          },
        }}
        axisBottom={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 90,
          legendPosition: "middle",
          legendOffset: 32,
        }}
        theme={theme}
        animate={!testMode}
        enableLabel={false}
        tooltip={showTooltip}
      />
    </ChartContainer>
  );
};

interface GenerationVsDemandChartProps {
  chartData: BarDatum[];
  height: string;
}

const GenerationVsDemandChart: React.FC<GenerationVsDemandChartProps> = ({
  chartData,
  height,
}) => {
  const chartLegend = getChartLegend(chartData as unknown as ChartData[]);
  return (
    <ChartWrapper height={height}>
      <Container>
        <MetricTooltip>
          <Tooltip
            text={
              "The energy generation of an on-site installation vs. the on-site demand for that site, broken down on a half-hourly basis for a “typical” summer day.\n\nSubmitted half hourly demand data improves accuracy."
            }
          />
        </MetricTooltip>
        <Header>
          <Title>Generation vs. on-site demand (typical summer day)</Title>
          <ChartLegendContainer>
            <ChartLegend chartLegend={chartLegend} />
          </ChartLegendContainer>
        </Header>
        <Chart>
          <ResponsiveBarChart
            chartData={chartData}
            colors={Object.values(chartLegend)}
            keys={Object.keys(chartLegend)}
            theme={theme}
          />
        </Chart>
      </Container>
    </ChartWrapper>
  );
};

const MetricTooltip = styled.div`
  align-self: flex-end;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  background: #ffffff;
  padding: 16px;
  border-radius: 8px;
  padding-top: 0px;
`;

const Title = styled.span`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-style: normal;
  font-weight: 600;
  font-size: 20px;
  color: #00022f;
`;

const Header = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const ChartContainer = styled.div`
  width: 100%;
  height: 295px;
  flex-grow: 1;
`;

const ChartLegendContainer = styled.div``;

export default GenerationVsDemandChart;
